import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import TemplatePicker from "components/admin/TemplatePicker";
import SelectField from "components/admin/SelectField";

/** Nhãn cho phần tử thứ 3 của dòng yêu cầu, theo từng mã. */
const THIRD_LABEL = {
  3: "Cấp kỹ năng tối thiểu",
  6: "Lev tiến hoá tối thiểu",
  7: "Cấp cường hoá tối thiểu",
  21: "Cấp pet tối thiểu",
};

/** Gợi ý ngắn hiện dưới tên mã, cho admin biết dòng này đếm cái gì. */
const HINTS = {
  0: "Đếm mỗi lần hạ một con quái đúng loài đã chọn",
  1: "Cấp cao nhất trong các pet của người chơi",
  5: "Đếm mỗi lần hạ đúng con boss đã chọn",
  9: "Đếm mỗi lần trả năng lượng để vào đánh boss, không cần hạ được",
  10: "Đang GIỮ đủ số lượng trong túi — vật phẩm không bị trừ khi trả nhiệm vụ",
  11: "Lượt cao nhất từng đạt ở ải",
  16: "Chỉ cần đi tới và bấm vào NPC",
  21: "Hiến tế ở NPC Sứ Giả Thiên Thần, pet bị xoá vĩnh viễn",
  22: "Boss map (thần chết) và boss theo giờ. Không tính boss ải / nhiệm vụ / sự kiện",
};

/**
 * Sửa cột `task` của một nhiệm vụ: mảng các dòng [mã, số_lượng, tham_số].
 * `meta.requests` từ GET /api/admin/tasks/meta cho biết mỗi mã cần mấy số và tham số là loại id gì.
 * Bố cục bám theo GiftEditor để hai khối trong cùng hộp thoại trông giống nhau.
 */
function RequestEditor({ value, onChange, meta }) {
  const lines = value || [];
  const specs = meta?.requests || [];
  const specOf = (code) => specs.find((s) => s.code === code);

  const setLine = (index, next) => {
    const copy = lines.map((l) => [...l]);
    copy[index] = next;
    onChange(copy);
  };

  const addLine = (code) => {
    const spec = specOf(code);
    if (!spec) return;
    const line = [code, 1];
    while (line.length < spec.length) line.push(0);
    onChange([...lines, line]);
  };

  /** Đổi mã của một dòng: giữ số lượng, cắt hoặc thêm phần tử cho khớp độ dài mã mới. */
  const changeCode = (index, code) => {
    const spec = specOf(code);
    const old = lines[index];
    const line = [code, old[1] > 0 ? old[1] : 1];
    while (line.length < (spec?.length || 2)) line.push(old[line.length] ?? 0);
    setLine(index, line.slice(0, spec?.length || 2));
  };

  const thirdField = (index, line, spec) => {
    const current = line[2] ?? 0;
    if (spec.refKind === "item" || spec.refKind === "pet") {
      return (
        <TemplatePicker
          kind={spec.refKind}
          label={spec.refKind === "item" ? "Tìm vật phẩm theo tên" : "Tìm quái / pet theo tên"}
          value={current}
          onChange={(id) => setLine(index, [line[0], line[1], Number(id) || 0])}
          fullWidth
        />
      );
    }
    if (spec.refKind === "npc") {
      return (
        <SelectField
          fullWidth
          label="NPC cần gặp"
          value={(meta?.npcs || []).some((n) => n.id === current) ? current : ""}
          onChange={(e) => setLine(index, [line[0], line[1], Number(e.target.value)])}
        >
          {(meta?.npcs || []).map((n) => (
            <MenuItem key={n.id} value={n.id}>
              {n.id} — {n.name}
            </MenuItem>
          ))}
        </SelectField>
      );
    }
    const label =
      THIRD_LABEL[line[0]] ||
      (spec.refKind === "boss" ? "Mã boss" : spec.refKind === "task" ? "Mã nhiệm vụ" : "Tham số");
    return (
      <MDInput
        fullWidth
        type="number"
        label={label}
        value={current}
        helperText={
          spec.refKind === "boss"
            ? "1-10 thần chết · 11-15 boss ải · 16 Thú cưng du côn"
            : undefined
        }
        onChange={(e) => setLine(index, [line[0], line[1], Number(e.target.value) || 0])}
      />
    );
  };

  return (
    <MDBox>
      <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
        Yêu cầu hoàn thành
      </MDTypography>

      {lines.length === 0 && (
        <MDTypography variant="caption" color="error" display="block" mb={1}>
          Chưa có dòng yêu cầu nào — nhiệm vụ sẽ hoàn thành ngay khi nhận.
        </MDTypography>
      )}

      {lines.map((line, index) => {
        const spec = specOf(line[0]);
        const hasThird = spec && spec.length > 2;
        return (
          <MDBox
            /* eslint-disable-next-line react/no-array-index-key */
            key={index}
            border="1px solid"
            borderColor="divider"
            borderRadius="lg"
            p={1.5}
            mb={1}
          >
            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
              <MDBox flexGrow={1} lineHeight={1.2}>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {spec?.label || "Mã không rõ"}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {HINTS[line[0]] || ""}
                </MDTypography>
              </MDBox>
              <Tooltip title="Bỏ dòng này">
                <IconButton
                  color="error"
                  onClick={() => onChange(lines.filter((_, i) => i !== index))}
                >
                  <Icon>delete</Icon>
                </IconButton>
              </Tooltip>
            </MDBox>

            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={12} sm={8}>
                <SelectField
                  fullWidth
                  label="Loại yêu cầu"
                  value={line[0]}
                  onChange={(e) => changeCode(index, Number(e.target.value))}
                >
                  {specs.map((s) => (
                    <MenuItem key={s.code} value={s.code} disabled={!s.alive && s.code !== line[0]}>
                      {s.label}
                      {!s.alive ? " — không dùng được" : ""}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Số lượng cần"
                  value={line[1]}
                  inputProps={{ min: 1 }}
                  onChange={(e) =>
                    setLine(index, [line[0], Number(e.target.value) || 0, ...line.slice(2)])
                  }
                />
              </Grid>
              {hasThird && (
                <Grid item xs={12}>
                  {thirdField(index, line, spec)}
                </Grid>
              )}
              <Grid item xs={12}>
                {spec && !spec.alive && (
                  <Chip
                    size="small"
                    color="error"
                    label="Người chơi không có cách hoàn thành dòng này — nên đổi loại khác"
                  />
                )}
              </Grid>
            </Grid>
          </MDBox>
        );
      })}

      <MDBox p={1.5} borderRadius="lg" sx={{ bgcolor: "action.hover" }}>
        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
          <MDTypography variant="caption" fontWeight="medium" flexGrow={1}>
            Bấm vào loại yêu cầu muốn thêm:
          </MDTypography>
          {lines.length > 0 && (
            <MDButton variant="text" color="error" size="small" onClick={() => onChange([])}>
              Xoá hết
            </MDButton>
          )}
        </MDBox>
        <MDBox display="flex" flexWrap="wrap" gap={1}>
          {specs
            .filter((s) => s.alive)
            .map((s) => (
              <Tooltip key={s.code} title={HINTS[s.code] || s.label}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => addLine(s.code)}
                >
                  {s.label}
                </MDButton>
              </Tooltip>
            ))}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

RequestEditor.propTypes = {
  value: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  onChange: PropTypes.func.isRequired,
  meta: PropTypes.shape({
    requests: PropTypes.arrayOf(PropTypes.object),
    npcs: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default RequestEditor;
